/**
 * Phase 1 spine — the per-program 4-KPI spine (the flagged "no per-program KPI
 * spine" gap). Renders the SDS scorecard at the top of a program as one
 * SpecraMetricStrip:
 *
 *   ① On-time delivery   ② CPARS / QASP score   ③ Funding consumed   ④ SDS audit checks passed
 *
 * Each tile's value, target and tone come from one computation in
 * kpiReadings (SAD-998), with its direction stated; the target is printed on
 * the tile and a tooltip says what it counts and over what window.
 *
 * Projection-aware: it composes useView for the no_view gate, and the two
 * sensitive KPI values (customer scores, financial funding consumption) route
 * through <MaskedField> — so a Masked/Simplified viewer still sees each KPI's
 * status (a Summary signal) but the underlying figures read "Restricted",
 * never leaked. On-time and audit-pass are not financial and show to everyone.
 *
 * On-time comes from the contract model (D104, D112 #6): pangaea's `on_time`
 * counts over every contract period (cumulative) by default, or over the one
 * period `periodId` names (that period plus open dates carried over) — the same
 * window, and the same request, as the Deliverables strip.
 *
 * Each tile links to the section that feeds it.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton, SpecraInlineBanner, SpecraMetricStrip, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { MaskedField } from '../../../lib/access/MaskedField';
import { financialSectionVisible, programRef } from '../../../lib/access/types';
import { useView } from '../../../lib/access/useView';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { OccurrenceWindow, useOccurrences } from '../../../pages/Delivery/Deliverables/hooks';
import { cmSpan } from '../../../pages/Delivery/Deliverables/lib/format';
import { auditReading, fundingReading, ON_TIME_TARGET_PCT, onTimeReading, SCORE_TARGET, scoreReading, twoWayOnTime } from './kpiReadings';
import { useProgramFinancialSummary } from './useProgramFinancialSummary';
// Default funding-alert threshold when a program hasn't overridden it —
// mirrors ryugu's defaultFundingAlertThresholdPct (financial_health.go).
const DEFAULT_FUNDING_ALERT_THRESHOLD_PCT = 75;
const STATUS_TONE = {
    green: 'success',
    red: 'danger',
    nodata: 'neutral'
};
// The section that feeds each KPI; its tile links there.
const SECTION = {
    onTime: 'deliverables',
    customer: 'quality',
    financial: 'financial',
    compliance: 'audits'
};
// Labels only: the strip holds its height while the figures load.
const LABEL = {
    onTime: 'On-time delivery',
    customer: 'CPARS / QASP score',
    financial: 'Funding consumed',
    compliance: 'SDS audit checks passed'
};
const LOADING_METRICS = Object.values(LABEL).map((label)=>({
        id: label,
        label,
        value: null
    }));
export function KpiSpine({ programId, periodId = '' }) {
    const ref = programRef(programId);
    const view = useView(ref);
    const { data, isLoading, isError } = useProgramFinancialSummary(programId);
    // The program's KPI window, the one ProgramKpiStrip counts (the same
    // request, so one fetch serves both); page_size 1 asks for the counts, not
    // the rows. Its own loading state lives in the On-Time tile, never the
    // whole spine.
    const occ = useOccurrences(programId, periodId ? {
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: periodId,
        pageSize: 1
    } : {
        window: OccurrenceWindow.ALL,
        pageSize: 1
    });
    if (view.viewMode === 'no_view') return null;
    if (isError) {
        // Distinct from "no data yet" — a failed RPC must not render as compliant zeros.
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            "data-testid": Testids.deliveryKpi.spine,
            tone: "danger",
            variant: "framed",
            children: "Couldn't load program health."
        });
    }
    if (isLoading) {
        return /*#__PURE__*/ _jsx(SpecraMetricStrip, {
            "data-testid": Testids.deliveryKpi.spine,
            loading: true,
            metrics: LOADING_METRICS
        });
    }
    const kpi = data?.kpiSnapshot;
    const health = data?.financialHealth;
    // Purposeful empty state — a bare "—" reads as broken; this reads as "not
    // measured yet" (the value only lands once the program has a KPI snapshot).
    const noData = 'No data yet';
    // Financial is nav-hidden for a viewer who can see neither figure it
    // exposes; the tile must not offer a door the product deliberately closed.
    const financialLinkable = financialSectionVisible(view.fieldMasked);
    const thresholdPct = health?.thresholdPct ?? DEFAULT_FUNDING_ALERT_THRESHOLD_PCT;
    // Sensitive values route through <MaskedField>; non-sensitive render raw.
    const masked = (fieldName, node)=>/*#__PURE__*/ _jsx(MaskedField, {
            fieldName: fieldName,
            resource: ref,
            children: node
        });
    // Financial Health is computed independently of the KPI snapshot (it comes
    // from recorded financial months + funding, not the kpi_snapshot rollup),
    // so this tile doesn't depend on `kpi` being present and is shared by both
    // branches below.
    const financialHasData = !!health?.hasFunding && !!health?.hasCurrent;
    // Masked ⇒ neutral, like no-data: a masked viewer's funding_consumed_pct
    // and threshold_breached arrive zeroed (false), so deriving a status from
    // them would paint a false GREEN "on target" for a viewer who isn't
    // allowed the underlying value. Gate on the tokens this tile actually
    // consumes for its value/classification — not `financialLinkable`, which
    // only asks "can the viewer reach ANY financial figure" and stays true for
    // an exec_viewer who sees funding but not burn; that's a different question
    // from "can they see THIS tile's own numbers".
    const financialMasked = view.fieldMasked('funding_consumed_pct') || view.fieldMasked('threshold_breached');
    const funding = fundingReading({
        hasData: financialHasData,
        consumedPct: health?.fundingConsumedPct ?? 0,
        thresholdPct
    });
    const financialTile = {
        label: LABEL.financial,
        value: funding.pct !== null ? masked('funding_consumed_pct', `${funding.pct}%`) : noData,
        status: financialMasked ? 'nodata' : funding.status,
        note: `Alert at ${thresholdPct}%`,
        info: `Share of the program's funded amount spent so far, from the recorded financial months. Lower is better; the tile turns red once it reaches the ${thresholdPct}% alert.`,
        section: SECTION.financial,
        testid: Testids.deliveryKpi.tileFinancial
    };
    // On-Time Delivery, from the contract model: independent of the snapshot.
    // Value and tone are one reading of the same counts (SAD-998): overdue
    // dates not filed yet are misses, in the share itself. Owner 2026-11-03:
    // the share is the window's own; late dates carried over from an earlier
    // period are a figure of their own beside it, never in it.
    const lateCount = occ.data?.stateCounts.late ?? 0;
    const carriedLate = occ.data?.onTime?.carriedLate ?? 0;
    const ownLate = lateCount - carriedLate;
    const onTime = onTimeReading(occ.data?.onTime ? {
        onTime: occ.data.onTime.onTime,
        timingKnown: occ.data.onTime.timingKnown,
        overdue: ownLate
    } : lateCount > 0 ? {
        onTime: 0,
        timingKnown: 0,
        overdue: lateCount
    } : undefined);
    const onTimePct = onTime.pct;
    // R20-A: with an applied tracker, the same counts on the tracker's own
    // scope beside the documents' reading, and why they differ.
    const counts = occ.data?.stateCounts ?? {};
    const twoWay = occ.data?.onTime?.evidenceScoped ? twoWayOnTime({
        onTime: occ.data.onTime.onTime,
        timingKnown: occ.data.onTime.timingKnown,
        late: ownLate,
        untrackedLate: occ.data.onTime.untrackedLate - occ.data.onTime.carriedUntrackedLate,
        notCovered: counts.not_covered ?? 0,
        waived: counts.waived ?? 0,
        covered: counts.covered ?? 0
    }) : undefined;
    const datesInWindow = Object.values(occ.data?.stateCounts ?? {}).reduce((a, n)=>a + n, 0);
    const win = occ.data?.window;
    const windowText = !periodId ? 'all contract periods' : win && win.basis !== 'none' ? `the picked contract period (${cmSpan(win.startOn, win.endOn)})` : 'the picked contract period';
    const onTimeTile = {
        label: LABEL.onTime,
        value: occ.isLoading ? /*#__PURE__*/ _jsx(Skeleton, {
            className: "h-6 w-14"
        }) : occ.isError ? "Couldn't load" : onTimePct !== null ? `${onTimePct}%` : datesInWindow === 0 ? 'No dates yet' : 'None approved yet',
        status: occ.isError ? 'nodata' : onTime.status,
        note: `Target ${ON_TIME_TARGET_PCT}%`,
        extra: [
            ownLate > 0 ? `${ownLate} overdue` : '',
            carriedLate > 0 ? `${carriedLate} carried over` : '',
            twoWay?.tracker.pct != null ? `tracker's scope ${twoWay.tracker.pct}%` : ''
        ].filter(Boolean).join(' · ') || undefined,
        info: `Deliverables approved and filed on or before their due date, out of everything that has come due in ${windowText}: approvals with both dates known, plus overdue dates not filed yet, which count as misses.${periodId ? ' Late dates carried over from an earlier period are counted on their own ("carried over"), not in the share.' : ''}`,
        section: SECTION.onTime,
        testid: Testids.deliveryKpi.tileOnTime
    };
    // Presence comes from the backend's has_* flags, never value shapes —
    // a zero-valued real entry (0-score CPARS) is data.
    const score = scoreReading({
        hasCpars: !!kpi?.hasCpars,
        cpars: kpi?.latestCparsScore ?? 0,
        hasQasp: !!kpi?.hasQasp,
        qasp: kpi?.latestQaspScore ?? 0
    });
    // has_audit means an audit row exists; with zero scored points there is
    // nothing to pass or fail yet.
    const audit = auditReading({
        hasAudit: !!kpi?.hasAudit,
        pass: kpi?.latestAuditPass ?? 0,
        total: kpi?.latestAuditTotal ?? 0
    });
    // Masked compliance booleans arrive zeroed (false) — deriving a status
    // from them would paint a false "Off target" red for a viewer who isn't
    // allowed the underlying value. Masked ⇒ neutral, like no-data. Gate on
    // the booleans actually consumed (plus the score value) so a PARTIAL
    // carve-out — score visible, classification masked — can't repaint the
    // fabricated red this exists to remove.
    const customerMasked = view.fieldMasked('cpars_score') || view.fieldMasked('kpi2_cpars_compliant') || view.fieldMasked('kpi2_qasp_compliant');
    const tiles = [
        onTimeTile,
        {
            label: LABEL.customer,
            // Only the legs on record are named — the backend zero-fills absent
            // sources, so only has_* can tell "no QASP" from "QASP scored zero".
            value: score.text ? masked('cpars_score', score.text) : 'No scores yet',
            status: customerMasked ? 'nodata' : score.status,
            note: `Target ${SCORE_TARGET.toFixed(1)} or higher`,
            extra: !customerMasked && score.status === 'red' ? `${score.legs.filter((l)=>!l.meets).map((l)=>l.name).join(' and ')} below target` : undefined,
            info: `The latest CPARS evaluation and QASP assessment overall scores, out of 5. Each score on record must be ${SCORE_TARGET.toFixed(1)} or higher; a source with no score yet is left out.`,
            section: SECTION.customer,
            testid: Testids.deliveryKpi.tileCustomer
        },
        financialTile,
        {
            label: LABEL.compliance,
            value: kpi && audit.status !== 'nodata' ? `${kpi.latestAuditPass}/${kpi.latestAuditTotal}` : noData,
            status: audit.status,
            note: 'Target all checks',
            info: "Checks passed out of checks scored in the program's latest SDS audit that is still open. Every check must pass.",
            section: SECTION.compliance,
            testid: Testids.deliveryKpi.tileCompliance
        }
    ];
    const byId = new Map(tiles.map((t)=>[
            t.testid,
            t
        ]));
    const metrics = tiles.map((t)=>({
            id: t.testid,
            label: t.label,
            // A no-data tile states what is missing, never a bare dash.
            value: t.value,
            muted: t.status === 'nodata',
            // The target and what is overdue; the dot and figure carry the status.
            note: [
                t.note,
                t.extra
            ].filter(Boolean).join(' · '),
            tone: STATUS_TONE[t.status],
            info: t.info,
            href: t.section === SECTION.financial && !financialLinkable ? undefined : `/delivery/programs/${programId}/${t.section}`
        }));
    const strip = /*#__PURE__*/ _jsx(SpecraMetricStrip, {
        "data-testid": Testids.deliveryKpi.spine,
        getItemProps: (m)=>({
                'data-kpi-status': byId.get(m.id)?.status
            }),
        getItemTestId: (m)=>m.id,
        metrics: metrics,
        renderLink: renderRouterLink
    });
    // One wrapper whether or not the line shows, so the strip never remounts
    // when the counts arrive.
    const show = twoWay && !occ.isLoading && !occ.isError ? twoWay : undefined;
    const pct = (r)=>r.pct === null ? 'no data' : `${r.pct}%`;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            strip,
            show ? /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.deliveryKpi.onTimeTwoWay,
                children: [
                    "On time per the documents ",
                    pct(show.documents),
                    " (everything owed) · on the tracker's own scope ",
                    pct(show.tracker),
                    " (only the dates it carries). The difference:",
                    ' ',
                    show.trackerMissed,
                    " late ",
                    show.trackerMissed === 1 ? 'date' : 'dates',
                    " the tracker never carries; ",
                    show.notCovered,
                    " not covered by the evidence, ",
                    show.waived,
                    " waived and",
                    ' ',
                    show.covered,
                    " covered by another deliverable are in neither."
                ]
            }) : null
        ]
    });
}
